import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { tenantHref } from "@/lib/tenantPath";
import { useLanguage, useT, useLocalized } from "@/lib/LanguageContext";
import { dateLocale } from "@/i18n/dateLocale";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { Loader2, ArrowUpRight, Heart, MessageCircle } from "lucide-react";

const fmtDate = (d, lang) => {
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toLocaleDateString(dateLocale(lang), { day: "numeric", month: "long", year: "numeric" });
};

export default function JournalIndex() {
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");
  const loc = useLocalized();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed load must not look like "no articles yet" — that reads as the practice
  // has published nothing, when the true story is the read itself never came back.
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        setPosts(data);
      } catch {
        setLoadError(true);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-20 max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <span className="eyebrow">{t("journal.eyebrow")}</span>
          <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight">{t("journal.title")}</h1>
          <p className="mt-4 text-lg text-eh-muted">
            {t("journal.subtitle")}
          </p>
        </div>
        <div className="mt-14">
          {loading ? (
            <div className="text-eh-muted flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> {tc("actions.loading")}</div>
          ) : loadError ? (
            <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-10 text-center" data-testid="journal-load-error">
              {t("journal.loadError")}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-10 text-center">
              {t("journal.emptyState")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => {
                const title = loc(p, "title");
                const excerpt = loc(p, "excerpt");
                const tag = loc(p, "tag");
                return (
                  <Link key={p.id} to={tenantHref(`/journal/${p.slug}`)} className="group block" data-testid={`journal-card-${p.slug}`}>
                    {p.cover_url && (
                      <div className="overflow-hidden rounded-[1.75rem] aspect-[4/5] mb-5">
                        <img src={p.cover_url} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-eh-muted">
                      <span className="text-eh-sage-deep font-semibold">{tag}</span>
                      <span className="w-1 h-1 rounded-full bg-eh-border" />
                      <span>{fmtDate(p.created_at, lang)}</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-eh-ink mt-3 leading-tight group-hover:text-eh-sage-deep transition-colors">{title}</h3>
                    {excerpt && <p className="text-eh-muted mt-2 leading-relaxed line-clamp-3">{excerpt}</p>}
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <span className="inline-flex items-center gap-2 font-medium text-eh-ink group-hover:gap-3 transition-all">
                        {t("journal.readMore")} <ArrowUpRight size={14} />
                      </span>
                      <span className="inline-flex items-center gap-3 text-eh-muted text-xs">
                        <span className="inline-flex items-center gap-1"><Heart size={12} /> {p.likes_count || 0}</span>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </ThemedPageLayout>
  );
}
